import { createContext, ReactNode, useContext } from 'react';

import { useSession } from '@/lib/auth-client';

// Define user type based on better-auth structure
interface AuthUser {
  id: string;
  email: string;
  emailVerified: boolean;
  name: string;
  createdAt: Date;
  updatedAt: Date;
  image?: string | null;
  phoneNumber?: string | null;
  phoneNumberVerified?: boolean | null;
}

// Define session type based on better-auth structure
interface AuthSession {
  user: AuthUser;
  session: {
    id: string;
    userId: string;
    expiresAt: Date;
    token: string;
    createdAt: Date;
    updatedAt: Date;
  };
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  session: AuthSession | null;
  refetch: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending, refetch, error } = useSession();

  if (import.meta.env.DEV) {
    console.log('[AuthProvider] useSession', { isPending, error });
  }

  const value: AuthContextType = {
    isAuthenticated: !!session?.user,
    isLoading: isPending,
    user: session?.user || null,
    session: session || null,
    refetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}
